import {
  doc, runTransaction, Timestamp, serverTimestamp, deleteDoc, updateDoc, getDoc,
} from "firebase/firestore";

export const RESERVATION_TTL_MS = 2 * 60 * 1000; // 2 minutes

// ── Timezone-Safe Date Utilities & Constraints ──────────────────────────────
export const getTodayLocal = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const formatDateToISO = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};

export const formatToAppDateString = (dateOrStr) => {
  if (!dateOrStr) return "";
  let d;
  if (typeof dateOrStr === "string") {
    // If it's already in DD-MM-YYYY format, return as is
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateOrStr)) return dateOrStr;
    d = parseLocalDate(dateOrStr);
  } else {
    d = dateOrStr;
  }
  if (!d || isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

export const getMinSelectableDate = (offsetDays = 3) => {
  const today = getTodayLocal();
  const minDate = new Date(today);
  minDate.setDate(today.getDate() + offsetDays);
  return minDate;
};

export const validateInterviewDate = (dateStr, offsetDays = 3) => {
  if (!dateStr) {
    return { valid: false, error: "Please select an interview date." };
  }

  const selectedDate = parseLocalDate(dateStr);
  if (!selectedDate || isNaN(selectedDate.getTime())) {
    return { valid: false, error: "Please enter a valid date." };
  }

  const minDate = getMinSelectableDate(offsetDays);
  if (selectedDate < minDate) {
    return {
      valid: false,
      error: "You must reserve a slot at least 3 days after your application date.",
    };
  }

  const dayOfWeek = selectedDate.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return {
      valid: false,
      error: "Weekends (Saturday and Sunday) are not available for interviews.",
    };
  }

  return { valid: true };
};

// Persists across refresh and all tabs from the same browser (localStorage)
export const getOrCreateUserId = () => {
  try {
    let uid = localStorage.getItem("vbt_user_id");
    if (!uid) {
      uid = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("vbt_user_id", uid);
    }
    return uid;
  } catch {
    return Math.random().toString(36).slice(2);
  }
};

// Unique per browser tab; survives refresh within the same tab (sessionStorage)
export const getOrCreateTabId = () => {
  try {
    let tid = sessionStorage.getItem("vbt_tab_id");
    if (!tid) {
      tid = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("vbt_tab_id", tid);
    }
    return tid;
  } catch {
    return Math.random().toString(36).slice(2);
  }
};

// Deterministic doc ID — global across all job/opportunity applications for the same slot.
export const buildReservationDocId = (date, slotTime) =>
  `${date}_${slotTime.replace(/[: ]/g, "-")}`;

export const createSlotReservation = async (db, date, slotTime, userId, tabId) => {
  const dateValidation = validateInterviewDate(date, 3);
  if (!dateValidation.valid) {
    throw new Error(dateValidation.error);
  }

  const docId = buildReservationDocId(date, slotTime);
  const reservationRef = doc(db, "slotReservations", docId);
  const now = new Date();
  const expiresAt = new Date(now.getTime() + RESERVATION_TTL_MS);

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(reservationRef);

    if (snap.exists()) {
      const data = snap.data();
      const expired = data.expiresAt.toDate() <= now;
      // Lock ownership is per-tab, not per-user — two tabs of the same user are independent
      const isThisTab = data.tabId === tabId;

      if (data.status === "confirmed") throw new Error("SLOT_PERMANENTLY_TAKEN");
      // Block any tab that didn't create the lock, even if same userId
      if (!expired && !isThisTab) throw new Error("SLOT_TEMPORARILY_RESERVED");

      // This tab's own lock (or an expired one) — refresh/claim it
      transaction.update(reservationRef, {
        userId,
        tabId,
        status: "temporary",
        expiresAt: Timestamp.fromDate(expiresAt),
        updatedAt: serverTimestamp(),
      });
    } else {
      transaction.set(reservationRef, {
        interviewDate: date,
        slotTime,
        userId,
        tabId,
        status: "temporary",
        expiresAt: Timestamp.fromDate(expiresAt),
        createdAt: serverTimestamp(),
      });
    }
  });

  return { docId, expiresAt };
};

export const releaseReservation = (db, docId) =>
  deleteDoc(doc(db, "slotReservations", docId)).catch(() => {});

export const confirmReservation = (db, docId) =>
  updateDoc(doc(db, "slotReservations", docId), {
    status: "confirmed",
    confirmedAt: serverTimestamp(),
  }).catch(() => {});

// Restore an existing reservation for this user (any tab).
export const restoreReservation = async (db, docId, userId, tabId) => {
  try {
    const snap = await getDoc(doc(db, "slotReservations", docId));
    if (!snap.exists()) return null;
    const data = snap.data();
    const expiresAt = data.expiresAt.toDate();
    if (data.userId !== userId || data.status !== "temporary" || expiresAt <= new Date()) return null;
    // Hand the lock to the current tab
    await updateDoc(doc(db, "slotReservations", docId), { tabId, updatedAt: serverTimestamp() });
    return { ...data, expiresAt, docId };
  } catch {
    return null;
  }
};
